import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../assets/LogoInsurcloud_NoBackground.png';
import DeloitteLogo from '../../assets/Deloitte_LogoNoBackground.png';
import PageHeader from '../../components/PageHeader/PageHeader';
import './CommandCenter.css';

// Nav items matching the real sidebar sections
const NAV_ITEMS = [
  { label: 'Command Center', icon: 'bi-broadcast-pin', color: '#3b82f6', paths: ['/command-center', '/client/'] },
  { label: 'Daily Validations', icon: 'bi-calendar-check', color: '#10b981', paths: ['/validations'] },
  { label: 'Data Validations', icon: 'bi-database-check', color: '#06b6d4', paths: ['/data-validations'] },
  { label: 'Reports', icon: 'bi-file-earmark-spreadsheet', color: '#f59e0b', paths: ['/reports'] },
  { label: 'AI Tools', icon: 'bi-cpu-fill', color: '#8b5cf6', paths: ['/agent-smith', '/ai-tools', '/iris'] }
];

function SidebarOverlay({ activePath, visible }) {
  return (
    <div className={`cc-sidebar-overlay${visible ? ' cc-sidebar-visible' : ''}`}>
      {/* Logo header — fixed 95px height, matches real sidebar */}
      <div className="cc-sidebar-logo-header">
        <img src={Logo} alt="Logo" className="cc-sidebar-logo" />
      </div>

      {/* Nav items */}
      <div className="cc-sidebar-nav">
        {NAV_ITEMS.map((item) => {
          const isActive = item.paths.some(p => activePath.startsWith(p));
          return (
            <div
              key={item.label}
              className={`cc-sidebar-item${isActive ? ' cc-sidebar-item-active' : ''}`}
              style={{
                '--item-color': item.color,
                animationDelay: isActive ? '0.18s' : '0s',
              }}
            >
              <i
                className={`bi ${item.icon} cc-sidebar-item-icon`}
                style={{ color: isActive ? item.color : '#94a3b8' }}
              />
              <span>{item.label}</span>
              {isActive && <div className="cc-sidebar-item-bar" style={{ background: item.color }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CommandCenter() {
  const navigate = useNavigate();
  const { hasPermission, isAgentSmith } = useAuth();
  const [animating, setAnimating] = useState(false);
  const [activePath, setActivePath] = useState('');
  const pendingPath = useRef(null);

  const capabilities = [
    {
      icon: 'bi-grid-1x2-fill',
      title: 'Command Center',
      description: 'Real-time monitoring of queues, microservices and environments with instant insights',
      path: '/command-center',
      color: '#667eea'
    },
    {
      icon: 'bi-patch-check-fill',
      title: 'Daily Validations',
      description: 'Comprehensive system health overview with automated validation checks',
      path: '/validations',
      color: '#10b981'
    },
    {
      icon: 'bi-database-fill-check',
      title: 'Data Validations',
      description: 'ETL job monitoring and data integrity validation',
      path: '/data-validations',
      color: '#06b6d4'
    },
    {
      icon: 'bi-bar-chart-line-fill',
      title: 'Reports',
      description: hasPermission('reports.generate')
        ? 'Access reporting across all clients'
        : 'Access release status reporting',
      path: '/reports',
      color: '#f59e0b'
    },
    {
      icon: 'bi-cpu-fill',
      title: 'AI Tools',
      description: 'AI-powered incident resolution',
      path: '/ai-tools',
      color: '#8b5cf6',
      agentSmithOnly: true
    },
  ];

  const visibleCapabilities = capabilities.filter(cap => {
    if (cap.adminOnly && !hasPermission('reports.view')) return false;
    if (cap.agentSmithOnly && !isAgentSmith) return false;
    return true;
  });

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <div className="command-center">

      <PageHeader
        eyebrow="Insurcloud Platform"
        title="IC Control Tower"
        subtitle="Centralized monitoring and validation platform for insurance operations"
      />

      <div className="capabilities-grid">
        {visibleCapabilities.map((capability, index) => {
          return (
            <div
              key={index}
              className="capability-card"
              onClick={() => handleCardClick(capability.path)}
              style={{
                '--accent-color': capability.color,
                '--accent-color-hover': capability.color === '#f59e0b' ? '#d97706' : '#764ba2',
                '--cap-shadow': capability.color === '#f59e0b' ? 'rgba(245, 158, 11, 0.18)' : 'rgba(102, 126, 234, 0.2)'
              }}
            >
              <div className="card-accent-line" />
              {capability.adminOnly && (
                <span className="card-badge">Admin</span>
              )}
              <div className="card-icon" style={{ color: capability.color }}>
                <i className={`bi ${capability.icon}`} />
              </div>
              <h3 className="card-title">{capability.title}</h3>
              <p className="card-description">{capability.description}</p>
            </div>
          );
        })}
      </div>

      {/* Footer brand bar — Insurcloud (project) + Deloitte */}
      <footer className="cc-brand-footer">
        <span className="cc-brand-chip cc-brand-chip--sm">
          <img src={Logo} alt="Insurcloud" className="cc-brand-bar__logo cc-brand-bar__logo--sm" />
        </span>
        <span className="cc-brand-footer__divider" aria-hidden />
        <span className="cc-brand-footer__meta">Powered by</span>
        <span className="cc-brand-chip cc-brand-chip--sm">
          <img src={DeloitteLogo} alt="Deloitte" className="cc-brand-bar__logo cc-brand-bar__logo--sm cc-brand-bar__logo--deloitte" />
        </span>
        <div className="cc-brand-footer__copyright">
          Copyright © {new Date().getFullYear()} Deloitte Technology SA. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
